import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from './wysupp-icon-green-transparent.svg';
import { FaEnvelope, FaArrowLeft } from 'react-icons/fa';

function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_URL;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        setError('');

        try {
            const response = await fetch(`${apiUrl}/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.detail || `HTTP error! status: ${response.status}`);
            }

            setMessage(data.message || 'Password reset instructions sent.');
            setEmail('');

            const userIp = await fetch('https://api.ipify.org?format=json')
                .then(response => response.json())
                .then(data => data.ip)
                .catch(() => 'unknown IP');

            const passwordChangeRequestDetails = {
                log_description: `User has requested a password change at ${new Date()} from IP address: ${userIp}`,
                user_username: email,
            };

            await fetch(`${apiUrl}/logs/`, {   
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(passwordChangeRequestDetails),
            });

        } catch (err) {
            console.error("Forgot Password Error:", err);
            setError(err.message.includes("Failed to fetch") ? "Network error. Please check your connection." : "An error occurred. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="auth-page-container">
            <header className="auth-header">
                <button onClick={() => navigate('/')} className="back-button">
                    <FaArrowLeft /> Back to Login
                </button>
                <img src={logo} alt="Wysupp Logo" className="header-logo" />
            </header>

            <main className="auth-card-wrapper">
                <h1 className="card-title">Forgot Your Password?</h1>
                <p className="card-subtitle">
                    Enter your email address below, and we'll send you a link to reset your password.
                </p>
                
                <form onSubmit={handleSubmit} className="auth-form">
                    {message && <div className="form-feedback success">{message}</div>}
                    {error && <div className="form-feedback error">{error}</div>}

                    <div className="input-group">
                        <FaEnvelope className="input-icon" />
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="you@example.com"
                            disabled={isLoading}
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={isLoading}
                        className="action-button"
                    >
                        {isLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                </form>
            </main>
            <style>{`
                /* FIX 1: Ensures predictable sizing for all elements, fixing card width issues. */
                .auth-page-container, .auth-page-container * {
                    box-sizing: border-box;
                }

                :root {
                    --dark-bg: #1a202c; --card-bg: #2d3748; --text-color: #e2e8f0;
                    --light-text: #a0aec0; --green-accent: #48bb78; --border-color: #4a5568;
                    --input-bg: #1a202c; --error-color: #fca5a5; --error-bg: rgba(239, 68, 68, 0.1);
                    --success-color: #a7f3d0; --success-bg: rgba(52, 211, 153, 0.1);
                }
                .auth-page-container {
                    background-color: var(--dark-bg);
                    min-height: 100vh;
                    color: var(--text-color);
                    font-family: 'Inter', sans-serif;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 1rem;
                }
                .auth-header {
                    width: 100%;
                    max-width: 500px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }
                .header-logo { height: 30px; }
                .back-button {
                    background: transparent; color: var(--light-text); border: none; padding: 0;
                    font-weight: 600; cursor: pointer; transition: color 0.2s;
                    display: flex; align-items: center; gap: 0.5rem;
                }
                .back-button:hover { color: var(--text-color); }
                
                .auth-card-wrapper {
                    width: 100%;
                    max-width: 500px;
                    background-color: var(--card-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 12px;
                    padding: 2.5rem;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    text-align: center;
                }
                .card-title {
                    font-size: 1.75rem; font-weight: 700;
                    color: var(--text-color); margin-bottom: 0.5rem;
                }
                .card-subtitle {
                    font-size: 1rem; color: var(--light-text);
                    margin-bottom: 2rem; max-width: 400px;
                }
                
                .auth-form {
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    gap: 1.5rem;
                }

                .form-feedback {
                    width: 100%; padding: 0.75rem; border-radius: 6px;
                    font-size: 0.9rem; font-weight: 500;
                }
                .form-feedback.success { background-color: var(--success-bg); color: var(--success-color); }
                .form-feedback.error { background-color: var(--error-bg); color: var(--error-color); }

                .input-group { position: relative; }
                .input-group input {
                    width: 100%;
                    height: 2.5rem; /* FIX 2: Added for consistent input height. */
                    padding: 1rem 1rem 1rem 3rem;
                    background-color: var(--input-bg);
                    border: 1px solid var(--border-color);
                    border-radius: 6px;
                    color: var(--text-color);
                    font-size: 1rem;
                    transition: border-color 0.2s;
                }
                .input-group input:focus {
                    outline: none;
                    border-color: var(--green-accent);
                }
                
                .input-icon {
                    position: absolute;
                    top: 50%; left: 1rem;
                    transform: translateY(-50%);
                    color: var(--light-text);
                }
                
                .action-button {
                    width: 100%;
                    background-color: var(--green-accent); color: var(--dark-bg);
                    border: none; border-radius: 6px; padding: 0.8rem;
                    font-size: 1rem; font-weight: 600; cursor: pointer;
                    transition: background-color 0.2s; margin-top: 0.5rem;
                }
                .action-button:hover:not(:disabled) { background-color: #38a169; }
                .action-button:disabled { background-color: #4a5568; cursor: not-allowed; opacity: 0.7; }
            `}</style>
        </div>
    );
}

export default ForgotPassword;